import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  FormHelperText,
  Alert,
  DialogContentText,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import { 
  WhatsApp as WhatsAppIcon, 
  Print as PrintIcon, 
  Receipt as ReceiptIcon,
  Build as BuildIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '../../utils/api';
import { openWhatsAppWithTemplate, TEMPLATE_TYPES } from '../../utils/whatsappTemplates';
import CustomerDialog from '../customers/CustomerDialog';
import MaintenanceServiceSheet from './MaintenanceServiceSheet';
import MaintenanceStandardPrint from './MaintenanceStandardPrint';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import DepositPaymentDialog from './DepositPaymentDialog';
import QuoteDialog from './QuoteDialog';
import QuoteViewDialog from './QuoteViewDialog';
import MaintenanceDetails from './MaintenanceDetails';

const MaintenanceForm = ({ open, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [customerSearchAttempted, setCustomerSearchAttempted] = useState(false);
  const [errors, setErrors] = useState({});
  const [nextProtocolNumber, setNextProtocolNumber] = useState(null);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [successMessage, setSuccessMessage] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [mechanics, setMechanics] = useState([]);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [selectedPrintType, setSelectedPrintType] = useState(null);
  const [openDepositDialog, setOpenDepositDialog] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [depositData, setDepositData] = useState(null);
  const [openQuoteDialog, setOpenQuoteDialog] = useState(false);
  const [openQuoteViewDialog, setOpenQuoteViewDialog] = useState(false);
  const [hasQuotes, setHasQuotes] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedMaintenanceId, setSelectedMaintenanceId] = useState(null);
  const [openMaintenanceDetails, setOpenMaintenanceDetails] = useState(false);
  
  const initialFormData = {
    customerId: '',
    vehicleId: '',
    description: '',
    status: 'In Coda',
    estimatedCost: '0',
    deposit: '',
    priority: 'Normale',
    privateNotes: '',
    startDate: new Date()
  };

  const [formData, setFormData] = useState(initialFormData);

  // Aggiorna la data/ora ogni secondo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (open) {
      // Reset completo del form quando si apre il dialog
      resetForm();
      setSelectedCustomer(null);
      setCustomerSearchQuery('');
      setCustomerSearchResults([]);
      setCustomerSearchAttempted(false);
      setVehicles([]);
      fetchCustomers();
      fetchNextProtocolNumber();
      fetchMechanics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch vehicles when a customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      fetchVehicles(selectedCustomer.id);
      setFormData(prev => ({ ...prev, customerId: selectedCustomer.id }));
    } else {
      setVehicles([]);
      setFormData(prev => ({ ...prev, customerId: '', vehicleId: '' }));
    }
  }, [selectedCustomer]);

  // Check for quotes when customer or vehicle changes
  useEffect(() => {
    checkForQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customerId, formData.vehicleId]);

  const fetchNextProtocolNumber = async () => {
    try {
      // We'll get the max protocol number for the current year and add 1
      const res = await api.get('/maintenance');
      const currentYear = new Date().getFullYear();
      
      // Filter maintenances from the current year
      const currentYearMaintenances = res.data.filter(m => {
        const maintenanceDate = new Date(m.createdAt || m.startDate);
        return maintenanceDate.getFullYear() === currentYear;
      });
      
      const maxProtocol = currentYearMaintenances.length > 0 
        ? Math.max(...currentYearMaintenances.map(m => m.protocolNumber)) 
        : 0;
      setNextProtocolNumber(maxProtocol + 1);
    } catch (err) {
      console.error('Error fetching next protocol number:', err);
      setNextProtocolNumber(1); // Default to 1 if there's an error
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async (customerId) => {
    setLoading(true);
    try {
      const res = await api.get(`/vehicles/customer/${customerId}`);
      setVehicles(res.data);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMechanics = async () => {
    setLoading(true);
    try {
      const res = await api.get('/mechanics');
      setMechanics(res.data);
    } catch (err) {
      console.error('Error fetching mechanics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Converti in maiuscolo se è il campo description
    const newValue = name === 'description' ? value.toUpperCase() : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleCustomerSearch = async () => {
    if (!customerSearchQuery.trim()) {
      setCustomerSearchResults([]);
      setCustomerSearchAttempted(false);
      return;
    }
    
    setCustomerSearchAttempted(true);
    setLoading(true);
    try {
      const res = await api.get(`/customers/search/${encodeURIComponent(customerSearchQuery.trim())}`);
      setCustomerSearchResults(res.data || []);
    } catch (err) {
      console.error('Error searching customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearchQuery(`${customer.firstName} ${customer.lastName}${customer.email ? ` - ${customer.email}` : ''}`);
    setCustomerSearchResults([]);
    setCustomerSearchAttempted(false);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setCustomerSearchAttempted(false);
    setFormData(prev => ({ ...prev, customerId: '', vehicleId: '' }));
    setVehicles([]);
  };

  const handleCustomerSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomerSearch();
    }
  };

  // Funzione per caricare lo storico manutenzioni del cliente
  const fetchMaintenanceHistory = async (customerId) => {
    if (!customerId) return;
    setLoadingHistory(true);
    try {
      const res = await api.get(`/maintenance/customer/${customerId}`);
      const history = res.data || [];
      // Ordina per data (più recente per prima)
      const sortedHistory = history.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.startDate || 0);
        const dateB = new Date(b.createdAt || b.startDate || 0);
        return dateB - dateA; // Ordine decrescente (più recente per prima)
      });
      setMaintenanceHistory(sortedHistory);
    } catch (err) {
      console.error('Error fetching maintenance history:', err);
      setMaintenanceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Funzione per aprire il dialog dello storico manutenzioni
  const handleOpenHistoryDialog = () => {
    if (selectedCustomer) {
      fetchMaintenanceHistory(selectedCustomer.id);
      setOpenHistoryDialog(true);
    }
  };

  // Funzione per chiudere il dialog dello storico manutenzioni
  const handleCloseHistoryDialog = () => {
    setOpenHistoryDialog(false);
    setMaintenanceHistory([]);
  };

  // Funzione per gestire il click su una manutenzione nello storico
  const handleMaintenanceClick = (maintenanceId) => {
    setSelectedMaintenanceId(maintenanceId);
    setOpenMaintenanceDetails(true);
  };

  // Funzione per chiudere il dialog dei dettagli manutenzione
  const handleCloseMaintenanceDetails = () => {
    setOpenMaintenanceDetails(false);
    setSelectedMaintenanceId(null);
  };

  // Funzione per formattare la data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Data non valida';
      }
      return format(date, 'dd/MM/yyyy', { locale: it });
    } catch (error) {
      return 'Data non valida';
    }
  };

  // Funzione per ottenere il colore dello stato
  const getStatusColor = (status) => {
    const statusColors = {
      'In Coda': 'default',
      'In Corso': 'primary',
      'Attesa Ricambi': 'warning',
      'Attesa Cliente': 'info',
      'Da Chiamare': 'secondary',
      'Da Ritirare': 'success',
      'Sollecitato': 'error',
      'Parcheggio': 'default',
      'Consegnato': 'success'
    };
    return statusColors[status] || 'default';
  };

  // eslint-disable-next-line no-unused-vars
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customerId) newErrors.customerId = 'Seleziona un cliente';
    if (!formData.vehicleId) newErrors.vehicleId = 'Seleziona un veicolo';
    if (!formData.description) newErrors.description = 'Inserisci una descrizione del problema';
    if (!formData.estimatedCost || formData.estimatedCost === '0') {
      newErrors.estimatedCost = 'Inserisci un costo stimato';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Funzione per resettare il form
  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({});
    setSuccessMessage('');
    setOpenDepositDialog(false);
    setLoading(false);
    setSelectedCustomer(null);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setCustomerSearchAttempted(false);
    setVehicles([]);
    setDepositData(null);
    setPrintDialogOpen(false);
    setPrintPreviewOpen(false);
    setSelectedPrintType(null);
  };

  // Funzione per chiudere la dialog
  const handleClose = () => {
    resetForm();
    if (onClose) onClose();
  };

  // Funzione per annullare
  const handleCancel = () => {
    resetForm();
    if (onClose) onClose();
  };

  // Funzione per salvare la manutenzione, con o senza dati di acconto
  const saveMaintenance = async (depositInfo = null) => {
    setLoading(true);
    setErrors({});
    try {
      let dataToSave = { ...formData };
      if (depositInfo) {
        // Se c'è acconto, aggiungi i dati con i nomi richiesti dal backend
        dataToSave = {
          ...dataToSave,
          depositPaymentMethodId: depositInfo.paymentMethodId,
          depositAccountId: depositInfo.accountId,
          depositDate: depositInfo.paymentDate
        };
      } else {
        // Se non c'è acconto, rimuovi i campi di pagamento
        delete dataToSave.paymentMethodId;
        delete dataToSave.accountId;
        delete dataToSave.paymentDate;
        delete dataToSave.depositPaymentMethodId;
        delete dataToSave.depositAccountId;
        delete dataToSave.depositDate;
        // Se deposit è vuoto o non numerico, impostalo a null
        if (!dataToSave.deposit || isNaN(Number(dataToSave.deposit))) {
          dataToSave.deposit = null;
        }
      }
      const savedMaintenance = await onSave(dataToSave);
      
      // Se la manutenzione è stata salvata con successo e abbiamo un preventivo,
      // aggiorna il maintenanceId nei preventivi
      console.log('🔍 Controllo aggiornamento preventivi:', {
        savedMaintenance: savedMaintenance,
        hasId: savedMaintenance && savedMaintenance.id,
        selectedCustomer: selectedCustomer,
        selectedCustomerId: selectedCustomer && selectedCustomer.id,
        vehicleId: formData.vehicleId,
        vehicleIdType: typeof formData.vehicleId
      });
      
      if (savedMaintenance && savedMaintenance.id && selectedCustomer && formData.vehicleId) {
        try {
          console.log('🔄 Chiamata endpoint aggiornamento preventivi:', {
            maintenanceId: savedMaintenance.id,
            customerId: selectedCustomer.id,
            vehicleId: parseInt(formData.vehicleId)
          });
          
          const updateResponse = await api.put('/quotes/update-maintenance-id', {
            maintenanceId: savedMaintenance.id,
            customerId: selectedCustomer.id,
            vehicleId: parseInt(formData.vehicleId)
          });
          
          console.log('✅ Preventivi aggiornati con maintenanceId:', updateResponse.data);
        } catch (error) {
          console.error('⚠️ Errore nell\'aggiornamento preventivi:', error);
          // Non bloccare il salvataggio per questo errore
        }
      } else {
        console.log('⚠️ Condizioni non soddisfatte per aggiornamento preventivi');
      }
      
      resetForm();
      handleClose();
    } catch (err) {
      setErrors({ general: err.message || 'Errore durante il salvataggio' });
    } finally {
      setLoading(false);
    }
  };

  // Submit principale
  const handleSubmit = async () => {
    console.log("🔥 handleSubmit chiamato");
    setErrors({});
    setLoading(true);
    // Validazione
    const newErrors = {};
    if (!formData.customerId) {
      newErrors.customerId = 'Seleziona un cliente';
    }
    if (!formData.vehicleId) {
      newErrors.vehicleId = 'Seleziona un veicolo';
    }
    if (!formData.description) {
      newErrors.description = 'Inserisci una descrizione';
    }
    if (!formData.estimatedCost || formData.estimatedCost === '0') {
      newErrors.estimatedCost = 'Inserisci un costo stimato';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }
    if (formData.deposit && Number(formData.deposit) > 0) {
      setDepositData({
        ...formData,
        vehicleId: parseInt(formData.vehicleId),
        startDate: new Date().toISOString(),
        customerName: selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : '',
        customerEmail: selectedCustomer ? selectedCustomer.email : '',
        customerPhone: selectedCustomer ? selectedCustomer.phone : '',
        bikeModel: vehicles.find(v => v.id === parseInt(formData.vehicleId))?.model || '',
        bikeBrand: vehicles.find(v => v.id === parseInt(formData.vehicleId))?.brand || '',
        priority: ['Bassa', 'Normale', 'Alta', 'Urgente'].includes(formData.priority) 
          ? formData.priority 
          : 'Normale',
        deposit: parseFloat(formData.deposit || '0'),
        estimatedCost: parseFloat(formData.estimatedCost || '0')
      });
      setOpenDepositDialog(true);
      return;
    }
    await saveMaintenance();
  };

  // Salvataggio con acconto
  const handleDepositSave = async (depositInfo) => {
    await saveMaintenance(depositInfo);
  };

  // Gestione preventivo
  const handleQuoteSave = async (quoteData) => {
    try {
      // Salva il preventivo nel database
      // eslint-disable-next-line no-unused-vars
      const response = await api.post('/quotes', quoteData);
      
      // Aggiorna la descrizione della manutenzione con i prodotti del preventivo
      const productDescriptions = quoteData.items.map(item => 
        `${item.productCode} - ${item.productName} (Qty: ${item.quantity})`
      ).join(' - ');
      
      setFormData(prev => ({
        ...prev,
        description: productDescriptions,
        estimatedCost: quoteData.total.toString()
      }));
      
      setSuccessMessage('Preventivo creato e inserito nella manutenzione con successo!');
      setHasQuotes(true); // Aggiorna lo stato per mostrare il pulsante visualizza
    } catch (error) {
      console.error('Errore nel salvataggio preventivo:', error);
      setErrors({ general: 'Errore nel salvataggio del preventivo' });
    }
  };

  // Verifica se esistono preventivi per la manutenzione corrente
  const checkForQuotes = async () => {
    if (!formData.customerId || !formData.vehicleId) {
      setHasQuotes(false);
      return;
    }

    try {
      // Cerca solo preventivi standalone (senza mantenanceId) per cliente e veicolo
      const response = await api.get(`/quotes?customerId=${formData.customerId}&vehicleId=${formData.vehicleId}&standalone=true`);
      setHasQuotes(response.data && response.data.length > 0);
    } catch (error) {
      console.error('Errore nel controllo preventivi:', error);
      setHasQuotes(false);
    }
  };

  // Gestione importazione preventivo
  const handleImportQuote = async (quoteData) => {
    try {
      // Aggiorna la descrizione della manutenzione con i prodotti del preventivo
      const productDescriptions = quoteData.items.map(item => 
        `${item.productCode} - ${item.productName} (Qty: ${item.quantity})`
      ).join(' - ');
      
      setFormData(prev => ({
        ...prev,
        description: productDescriptions,
        estimatedCost: quoteData.total.toString()
      }));
      
      setSuccessMessage('Preventivo importato nella manutenzione con successo!');
      setOpenQuoteViewDialog(false);
    } catch (error) {
      console.error('Errore nell\'importazione preventivo:', error);
      setErrors({ general: 'Errore nell\'importazione del preventivo' });
    }
  };

  const handleSendWhatsApp = async () => {
    if (!selectedCustomer) return;
    
    // Prepare data for template variables
    const templateData = {
      customerName: selectedCustomer.firstName,
      protocolNumber: nextProtocolNumber,
      vehicleModel: formData.vehicleId ? vehicles.find(v => v.id === parseInt(formData.vehicleId))?.model || '' : '',
      problemDescription: formData.description || '',
      deposit: formData.deposit || '0'
    };
    
    // Use the registration template
    await openWhatsAppWithTemplate(
      selectedCustomer.phone,
      TEMPLATE_TYPES.MAINTENANCE_REGISTRATION,
      templateData,
      selectedCustomer.countryCode
    );
  };

  const handleNewCustomerClick = () => {
    setOpenCustomerDialog(true);
  };

  const handleEditCustomerClick = () => {
    if (selectedCustomer) {
      setOpenCustomerDialog(true);
    }
  };

  const handleCustomerDialogClose = () => {
    setOpenCustomerDialog(false);
  };

  const handleCustomerSave = (savedCustomer) => {
    // Aggiorna o aggiungi il cliente alla lista
    setCustomers(prev => {
      const existingIndex = prev.findIndex(c => c.id === savedCustomer.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = savedCustomer;
        return updated;
      } else {
        return [savedCustomer, ...prev];
      }
    });
    // Seleziona automaticamente il cliente salvato
    setSelectedCustomer(savedCustomer);
    setCustomerSearchQuery(`${savedCustomer.firstName} ${savedCustomer.lastName}${savedCustomer.email ? ` - ${savedCustomer.email}` : ''}`);
    setCustomerSearchResults([]);
    setCustomerSearchAttempted(false);
    // Chiudi il dialog
    setOpenCustomerDialog(false);
  };

  const handlePrint = () => {
    setPrintDialogOpen(true);
  };

  // eslint-disable-next-line no-unused-vars
  const handlePrintTypeSelect = (type) => {
    setSelectedPrintType(type);
    setPrintDialogOpen(false);
    setPrintPreviewOpen(true);
  };

  const handlePrintToPdf = async (type) => {
    setPrintDialogOpen(false);
    setPrintPreviewOpen(true);
    setSelectedPrintType(type);

    // Attendiamo che il contenuto sia renderizzato
    await new Promise(resolve => setTimeout(resolve, 100));

    const elementId = type === 'service-sheet' ? 'service-sheet' : 'standard-print';
    const element = document.getElementById(elementId);
    if (!element) {
      console.error(`Elemento con id ${elementId} non trovato`);
      return;
    }

    try {
      // Calcoliamo le dimensioni in pixel (1mm = 3.779527559055118 px a 96 DPI)
      const pxPerMm = 3.779527559055118;
      const dimensions = type === 'service-sheet' 
        ? { width: 90, height: 205 }
        : { width: 148, height: 210 };

      const canvas = await html2canvas(element, {
        scale: 4,
        useCORS: true,
        logging: false,
        width: dimensions.width * pxPerMm,
        height: dimensions.height * pxPerMm,
        windowWidth: dimensions.width * pxPerMm,
        windowHeight: dimensions.height * pxPerMm
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [dimensions.width, dimensions.height]
      });

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(
        imgData,
        'PNG',
        0,
        0,
        dimensions.width,
        dimensions.height,
        '',
        'FAST'
      );

      pdf.autoPrint();
      window.open(pdf.output('bloburl'), '_blank');
    } catch (error) {
      console.error('Errore durante la generazione del PDF:', error);
    }
  };

  const renderPrintDialog = () => (
    <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)}>
      <DialogTitle>Seleziona il tipo di stampa</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Scegli il formato di stampa desiderato:
        </DialogContentText>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => handlePrintToPdf('service-sheet')}
          >
            Stampa su scheda di lavoro
          </Button>
          <Button
            variant="outlined"
            onClick={() => handlePrintToPdf('standard')}
          >
            Stampa Standard
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPrintDialogOpen(false)}>Annulla</Button>
      </DialogActions>
    </Dialog>
  );

  const maintenanceData = {
    customerName: selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : '',
    customerPhone: selectedCustomer?.phone || '',
    bikeBrand: vehicles.find(v => v.id === parseInt(formData.vehicleId))?.brand || '',
    bikeModel: vehicles.find(v => v.id === parseInt(formData.vehicleId))?.model || '',
    description: formData.description,
    deposit: formData.deposit,
    estimatedCost: formData.estimatedCost,
    protocolNumber: nextProtocolNumber
  };

  const renderPrintPreview = () => (
    <Dialog
      open={printPreviewOpen}
      onClose={() => setPrintPreviewOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Anteprima Stampa</DialogTitle>
      <DialogContent>
        {selectedPrintType === 'service-sheet' ? (
          <MaintenanceServiceSheet
            maintenance={maintenanceData}
            nextProtocolNumber={nextProtocolNumber}
          />
        ) : (
          <MaintenanceStandardPrint 
            maintenance={maintenanceData}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPrintPreviewOpen(false)}>Chiudi</Button>
        <Button 
          variant="contained"
          onClick={() => handlePrintToPdf(selectedPrintType)}
        >
          Stampa
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography>Nuova Manutenzione</Typography>
          <Typography variant="body2" color="text.secondary">
            {format(currentDateTime, 'dd/MM/yyyy HH:mm:ss', { locale: it })}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Numero Protocollo"
              value={nextProtocolNumber || ''}
              fullWidth
              disabled
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="priority-label">Priorità</InputLabel>
              <Select
                labelId="priority-label"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                label="Priorità"
              >
                <MenuItem value="Bassa">Bassa</MenuItem>
                <MenuItem value="Normale">Normale</MenuItem>
                <MenuItem value="Alta">Alta</MenuItem>
                <MenuItem value="Urgente">Urgente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                {selectedCustomer && (
                  <Tooltip title="Visualizza storico manutenzioni">
                    <IconButton
                      onClick={handleOpenHistoryDialog}
                      color="primary"
                      sx={{ mt: 1 }}
                    >
                      <BuildIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Cerca Cliente"
                    value={customerSearchQuery}
                    onChange={(e) => {
                      setCustomerSearchQuery(e.target.value);
                      setCustomerSearchAttempted(false);
                      setCustomerSearchResults([]);
                    }}
                    onKeyPress={handleCustomerSearchKeyPress}
                    error={!!errors.customerId}
                    helperText={errors.customerId || (customerSearchAttempted && customerSearchResults.length === 0 ? 'Nessun cliente trovato' : 'Premi Invio per cercare')}
                    required
                    margin="normal"
                    InputProps={{
                      endAdornment: (
                        <>
                          {loading && <CircularProgress size={20} />}
                          {selectedCustomer && !loading && (
                            <IconButton
                              size="small"
                              onClick={handleClearCustomer}
                              sx={{ mr: -1 }}
                              edge="end"
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </>
                      )
                    }}
                  />
                  {customerSearchResults.length > 0 && (
                    <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                      <List dense>
                        {customerSearchResults.map((customer) => (
                          <ListItem
                            key={customer.id}
                            button
                            onClick={() => handleCustomerSelect(customer)}
                            sx={{ 
                              '&:hover': { bgcolor: 'action.hover' },
                              color: '#212121 !important'
                            }}
                          >
                            <ListItemText
                              primary={`${customer.firstName} ${customer.lastName}`}
                              secondary={customer.phone || customer.email || ''}
                              primaryTypographyProps={{ 
                                sx: { color: '#212121 !important', fontWeight: 500 } 
                              }}
                              secondaryTypographyProps={{ 
                                sx: { color: '#757575 !important' } 
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              </Box>
              {selectedCustomer ? (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleEditCustomerClick}
                  sx={{ mt: 2 }}
                >
                  Modifica Cliente
                </Button>
              ) : customerSearchQuery.trim() && !customerSearchAttempted ? (
                <IconButton
                  variant="outlined"
                  color="primary"
                  onClick={handleCustomerSearch}
                  sx={{ mt: 2, border: 1, borderColor: 'primary.main', borderRadius: 1 }}
                >
                  <SearchIcon />
                </IconButton>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleNewCustomerClick}
                  sx={{ mt: 2 }}
                >
                  Nuovo Cliente
                </Button>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth margin="normal" error={!!errors.vehicleId}>
              <InputLabel id="vehicle-label">Veicolo</InputLabel>
              <Select
                labelId="vehicle-label"
                name="vehicleId"
                value={formData.vehicleId}
                onChange={handleInputChange}
                label="Veicolo"
                disabled={!selectedCustomer}
              >
                {vehicles.map(vehicle => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.brand} {vehicle.model}
                  </MenuItem>
                ))}
              </Select>
              {errors.vehicleId && <FormHelperText>{errors.vehicleId}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ReceiptIcon />}
                onClick={() => setOpenQuoteDialog(true)}
                disabled={!selectedCustomer || !formData.vehicleId}
                sx={{ minWidth: 200 }}
              >
                Nuovo Preventivo
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ReceiptIcon />}
                onClick={() => setOpenQuoteViewDialog(true)}
                disabled={!selectedCustomer || !formData.vehicleId || !hasQuotes}
                sx={{ minWidth: 200 }}
              >
                Visualizza Preventivo
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Crea o visualizza preventivi dettagliati con prodotti dal magazzino
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Descrizione Problema"
              name="description"
              value={formData.description}
              onChange={(e) => {
                const input = e.target;
                const cursorPosition = input.selectionStart;
                const newValue = e.target.value.toUpperCase();
                
                // Aggiorna lo stato
                handleInputChange({
                  target: {
                    name: e.target.name,
                    value: newValue
                  }
                });
                
                // Ripristina la posizione del cursore dopo il re-render
                setTimeout(() => {
                  input.setSelectionRange(cursorPosition, cursorPosition);
                }, 0);
              }}
              multiline
              rows={4}
              fullWidth
              margin="normal"
              error={!!errors.description}
              helperText={errors.description}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Costo Stimato"
              name="estimatedCost"
              value={formData.estimatedCost}
              onChange={handleInputChange}
              type="number"
              fullWidth
              margin="normal"
              error={!!errors.estimatedCost}
              helperText={errors.estimatedCost}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>€</Typography>,
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Acconto"
              name="deposit"
              value={formData.deposit}
              onChange={handleInputChange}
              type="number"
              fullWidth
              margin="normal"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>€</Typography>,
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Note Private (opzionale)"
              name="privateNotes"
              value={formData.privateNotes}
              onChange={handleInputChange}
              multiline
              rows={2}
              fullWidth
              margin="normal"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Box>
        <Button 
          startIcon={<WhatsAppIcon />}
          onClick={handleSendWhatsApp}
          disabled={!selectedCustomer || !selectedCustomer.phone}
          color="success"
          variant="outlined"
        >
          Invia WhatsApp
        </Button>
          </Box>
        <Box>
          <Button onClick={handleCancel} sx={{ mr: 1 }}>
            Annulla
          </Button>
            <Button
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={!selectedCustomer}
              color="primary"
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Stampa
            </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </Box>
      </DialogActions>
      </Dialog>

      <CustomerDialog
        open={openCustomerDialog}
        handleClose={handleCustomerDialogClose}
        onSave={handleCustomerSave}
        customer={selectedCustomer}
      />

      <DepositPaymentDialog
        open={openDepositDialog}
        onClose={() => setOpenDepositDialog(false)}
        depositAmount={formData.deposit}
        onSave={handleDepositSave}
      />

      {renderPrintDialog()}
      {renderPrintPreview()}

      {/* Quote Dialog */}
      <QuoteDialog
        open={openQuoteDialog}
        onClose={() => setOpenQuoteDialog(false)}
        onSave={handleQuoteSave}
        customer={selectedCustomer}
        vehicle={vehicles.find(v => v.id === parseInt(formData.vehicleId))}
        maintenanceData={{
          protocolNumber: nextProtocolNumber,
          priority: formData.priority
        }}
      />

      {/* Quote View Dialog */}
      <QuoteViewDialog
        open={openQuoteViewDialog}
        onClose={() => setOpenQuoteViewDialog(false)}
        onImport={handleImportQuote}
        customerId={formData.customerId}
        vehicleId={formData.vehicleId}
        date={null} // Non necessario più
        maintenanceId={null} // Non ancora salvata
      />

      {/* Maintenance History Dialog */}
      <Dialog open={openHistoryDialog} onClose={handleCloseHistoryDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant="h6" component="span" sx={{ flex: 1 }}>
              Storico Manutenzioni - {selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : ''}
            </Typography>
            <IconButton
              onClick={() => {
                if (selectedCustomer) {
                  fetchMaintenanceHistory(selectedCustomer.id);
                }
              }}
              color="primary"
              disabled={loadingHistory}
              size="small"
              sx={{ ml: 2 }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : maintenanceHistory.length > 0 ? (
            <List>
              {maintenanceHistory.map((maintenance) => (
                <ListItem 
                  key={maintenance.id} 
                  button
                  onClick={() => handleMaintenanceClick(maintenance.id)}
                  divider
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1">
                          Protocollo #{maintenance.protocolNumber}
                        </Typography>
                        <Chip 
                          label={maintenance.status} 
                          color={getStatusColor(maintenance.status)} 
                          size="small" 
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span">
                          Data: {formatDate(maintenance.createdAt)}
                        </Typography>
                        <br />
                        <Typography variant="body2" component="span">
                          {maintenance.description && maintenance.description.length > 0
                            ? maintenance.description.substring(0, 100) + (maintenance.description.length > 100 ? '...' : '')
                            : 'Nessuna descrizione'}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center' }}>
              Nessuna manutenzione trovata per questo cliente
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance Details Dialog */}
      {selectedMaintenanceId && (
        <MaintenanceDetails
          open={openMaintenanceDetails}
          onClose={handleCloseMaintenanceDetails}
          maintenanceId={selectedMaintenanceId}
        />
      )}
    </>
  );
};

export default MaintenanceForm;